/**
 * iPASS MONEY APP 轉導入口（/ipass/entry）的「中繼」純函式。
 *
 * 問題：一卡通轉導過來的是我們自有網域的固定網址，會落在 iPASS APP 的 webview 或手機
 * 瀏覽器——都不是 LINE 內。LIFF 在外部瀏覽器會走 LINE Login，登入頁再喚起 LINE app、
 * LINE 再把人送回瀏覽器（新分頁，sessionStorage 全失）→ 又不是登入狀態 → 再登入……
 * 無限來回（2026-09-29 pre-prod 實測）。
 *
 * 做法比照簡訊邀請中繼頁（smsInviteOpen.ts）：不在 LINE 內時**不初始化 LIFF、不導登入**，
 * 改成把人帶進 `liff.line.me/{liffId}/ipass/entry?…`（自動試一次 line:// scheme ＋ 手點按鈕），
 * 進到 LINE 內再走正常流程。網址上的 iPASSdata / IV 原樣塞進 liff.state 帶過去。
 */
const LIFF_URL_PREFIX = "https://liff.line.me";
const LINE_SCHEME_PREFIX = "line://app";
const ENTRY_PATH = "/ipass/entry";
const AUTO_OPENED_KEY = "ipass_entry_auto_opened";

export const isIPassEntryPath = (
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): boolean => /^\/ipass\/entry\/?$/.test(pathname);

/**
 * 是否在 LINE 內建瀏覽器。用 UA 判斷（LINE 內建瀏覽器 UA 含 " Line/"），不靠 liff.isInClient()：
 * 這個判斷發生在 liff.init 之前，而且 LIFF 未初始化時 isInClient 的行為沒有保證。
 */
export const isInLineClient = (
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent : "",
): boolean => /\bLine\//i.test(ua);

/** 中繼模式＝在 /ipass/entry 且不在 LINE 內；此時 LineLoginProvider 旁路、頁面只負責帶進 LINE */
export const isIPassEntryRelayMode = (
  pathname?: string,
  ua?: string,
): boolean => isIPassEntryPath(pathname) && !isInLineClient(ua);

/** 落地 query：保留一卡通給的參數，拿掉 liffId（它已經在 liff.line.me 路徑上）與 LIFF 回跳殘留 */
const landingQuery = (search: string): string => {
  const q = new URLSearchParams(search);
  ["liffId", "liff.state", "liff.referrer", "code", "state"].forEach((k) => q.delete(k));
  return q.toString();
};

const landingPath = (search: string): string => {
  const q = landingQuery(search);
  return q ? `${ENTRY_PATH}?${q}` : ENTRY_PATH;
};

/** `https://liff.line.me/{liffId}/ipass/entry?iPASSdata=…&IV=…`（手點用，Universal Link 最穩） */
export const buildIPassEntryLiffUrl = (liffId: string, search: string): string | null =>
  liffId ? `${LIFF_URL_PREFIX}/${liffId}${landingPath(search)}` : null;

/** `line://app/{liffId}?liff.state=%2Fipass%2Fentry%3F…`（程式自動導向也能喚起 LINE） */
export const buildIPassEntryLineSchemeUrl = (liffId: string, search: string): string | null => {
  if (!liffId) return null;
  const q = new URLSearchParams();
  q.set("liff.state", landingPath(search));
  return `${LINE_SCHEME_PREFIX}/${liffId}?${q.toString()}`;
};

export const markIPassAutoOpened = (): void => {
  try {
    window.sessionStorage.setItem(AUTO_OPENED_KEY, "1");
  } catch {
    // sessionStorage 不可用時只是少一道保險
  }
};

/**
 * 要不要自動嘗試開 LINE：帶 liff.state（LIFF 回跳）、從 liff.line.me 轉來、或本 session
 * 已自動試過一次 → 不再自動跳，只留按鈕。這是迴圈的最後一道保險。
 */
export const shouldAutoOpenIPassLiff = (
  search: string = typeof window !== "undefined" ? window.location.search : "",
  referrer: string = typeof document !== "undefined" ? document.referrer : "",
): boolean => {
  if (new URLSearchParams(search).has("liff.state")) return false;
  if (/^https?:\/\/liff\.line\.me(\/|$)/i.test(referrer)) return false;
  try {
    if (window.sessionStorage.getItem(AUTO_OPENED_KEY) === "1") return false;
  } catch {
    // ignore
  }
  return true;
};
